// Upload Wizard Hook
import { useState, useCallback } from 'react';
import { FileType, FileStatus } from '@types';
import { UploadStep, UploadWizardData, UploadWizardContextType } from '../types';

const INITIAL_DATA: UploadWizardData = {
  files: [],
  groups: [],
  fileType: FileType.BEAT,
  fileStatus: FileStatus.WIP,
  beatstarsSellUrl: '',
  beatstarsPrice: '',
  collaborator: '',
};

const STEPS: UploadStep[] = ['files', 'grouping', 'type', 'metadata', 'review'];

export const useUploadWizard = (): UploadWizardContextType => {
  const [data, setDataState] = useState<UploadWizardData>(INITIAL_DATA);
  const [currentStep, setCurrentStep] = useState<UploadStep>('files');

  const setData = useCallback((updates: Partial<UploadWizardData> | ((prev: UploadWizardData) => Partial<UploadWizardData>)) => {
    setDataState((prev) => {
      const newUpdates = typeof updates === 'function' ? updates(prev) : updates;
      return { ...prev, ...newUpdates };
    });
  }, []);

  const nextStep = useCallback(() => {
    const currentIndex = STEPS.indexOf(currentStep);
    if (currentIndex < STEPS.length - 1) {
      setCurrentStep(STEPS[currentIndex + 1]);
    }
  }, [currentStep]);

  const prevStep = useCallback(() => {
    const currentIndex = STEPS.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(STEPS[currentIndex - 1]);
    }
  }, [currentStep]);

  const goToStep = useCallback((step: UploadStep) => {
    setCurrentStep(step);
  }, []);

  const reset = useCallback(() => {
    setDataState(INITIAL_DATA);
    setCurrentStep('files');

    import('@services/metadata.service').then(({ metadataService }) => {
      metadataService.clearAllCache();
    });

    if (typeof window !== 'undefined') {
      const fileInputs = document.querySelectorAll('input[type="file"]');
      fileInputs.forEach((input) => {
        if (input instanceof HTMLInputElement) {
          input.value = '';
        }
      });
    }
  }, []);

  return {
    data,
    currentStep,
    setData,
    nextStep,
    prevStep,
    goToStep,
    reset,
  };
};

export const UPLOAD_WIZARD_STEPS = STEPS;
